/**
 * History Rail — mount, unmount, and the rail state machine (HR-0).
 *
 * The ONE module `ui/shell.js` imports from this directory. Everything else
 * under `ui/history-rail/` is reachable only through here, which is what makes
 * the initiative removable by deleting the directory and one import.
 *
 * Contracts: `builder/docs/history-rail-contracts.md`.
 * Plan: `.claude/plans/history-rail-master-plan.md`.
 *
 * HR-0 delivers the SLOT and the way out — the rail mounts, the canvas reflows,
 * the left panel tucks, and every exit path restores what it changed. The
 * timeline is HR-1 and preview-on-select is HR-2, so `openHistoryRail()` here
 * paints a placeholder body on purpose rather than a stub list.
 */

import { eventBus } from '../../core/events.js';
import { store, WRITE_BLOCKER, readOnlyReasonFor } from '../../core/state.js';
import { createButton } from '../components.js';
import { icon } from '../icons.js';
import { reportError } from '../../core/report-error.js';
import { listProjectVersions, labelProjectVersion } from '../../templates/auth-api.js';
import { TemplateApiError } from '../../templates/template-api.js';
import { createVersionModel, clockTime, dayLabelOf } from './version-model.js';
import { createPreviewController } from './preview.js';
import { mountReviewBar } from './review-bar.js';
import { createRestoreController } from './restore.js';
import { createCompareController, describeChanges } from './compare.js';
import { getVersionState } from '../../core/version-state-cache.js';
import { showToast } from '../toast.js';
import { mountTimeline } from './timeline.js';

/**
 * Kill switch.
 *
 * DEVIATION FROM THE PLAN, recorded in `history-rail-error-log.md` and
 * contracts §0.1: the plan called for a `state.flags` entry "same shape as
 * agentReviewMode". That shape is wrong here. `state.flags` is persisted into
 * every bundle (`core/save-load.js`), is NOT in `LOCAL_ONLY_PATHS`
 * (`shared/collab-protocol.js:36`), and therefore feeds `hashableState` and
 * `contentHash` — which Version History folds runs on. A UI kill switch that can
 * move a project's content hash would unfold runs in the very surface this
 * initiative rebuilds. localStorage, per the `pb-collab-dev` precedent, cannot.
 *
 * HR-7 INVERTED IT. Through HR-6 this was a dark launch — absent meant OFF and
 * the modal was the fallback. The modal is deleted, so absent now means ON and
 * only the literal string '0' turns the rail off. The switch is kept rather
 * than deleted for exactly one reason, and it is written down so it can be
 * checked off: no PHYSICAL HANDSET has run
 * `builder/docs/history-rail-manual-checklist.md`. Retire the key when that
 * checklist records a real device.
 */
const FLAG_KEY = 'pb-history-rail';

/**
 * Below this the rail becomes a bottom DOCK rather than a side rail (HR-6) —
 * still in flow, still taking space from the canvas rather than covering it.
 * It is not a floating sheet: that would need `position: fixed`, which is the
 * vocabulary contract R1 forbids, and exempting the rail from its own rule the
 * moment the screen gets small is how the rule stops meaning anything.
 */
const MOBILE_DOCK_WIDTH = 900;

export function isHistoryRailEnabled() {
  try {
    return localStorage.getItem(FLAG_KEY) !== '0';
  } catch {
    // Expected-flow fallback: Safari private mode throws on localStorage
    // access. A kill switch that cannot be READ is not engaged — and since
    // HR-7 this is the only history surface there is, so a storage failure
    // must never be the reason someone cannot reach their versions.
    return true;
  }
}

/** @type {{slot: HTMLElement, keydown: Function}|null} */
let session = null;
const leftTuckOwners = new Set();

function syncLeftPanelTuck() {
  const left = document.querySelector('.pb-panel-left');
  if (!left) return null;
  if (leftTuckOwners.size) left.classList.add('pb-rail-tucked');
  else left.classList.remove('pb-rail-tucked');
  return left;
}

function setLeftPanelTuckOwner(owner, active) {
  if (active) leftTuckOwners.add(owner);
  else leftTuckOwners.delete(owner);
  return syncLeftPanelTuck();
}

/** Transient canvas-selection owner; never persisted as a layout preference. */
export function yieldLeftRailForCanvas() {
  setLeftPanelTuckOwner('canvas-selection', true);
}

export function restoreLeftRailAfterCanvas() {
  setLeftPanelTuckOwner('canvas-selection', false);
}

export function isHistoryRailOpen() {
  return session !== null;
}

/**
 * The rail is an ERRAND, not a residence (contract R2): it is never open on
 * boot and never restored on reload, so there is no persisted "was open" bit to
 * read here and there must never be one.
 *
 * @param {{projectId?: string}} [opts]
 * @returns {boolean} whether the rail opened
 */
export function openHistoryRail({ projectId = null } = {}) {
  if (session) return true;

  // HR-7: every `return false` below is a REFUSAL THE USER ASKED FOR, and each
  // one now says why. Through HR-6 they could be silent because the modal
  // caught them; the modal is gone, so a silent false is a History button that
  // does nothing — the single worst outcome available to this function.
  if (!isHistoryRailEnabled()) {
    showToast('Version history is switched off in this browser.', 'info');
    return false;
  }

  const slot = document.getElementById('pb-history-rail-slot');
  if (!slot) {
    reportError('History rail', new Error('#pb-history-rail-slot is missing from the shell'), {
      user: 'toast',
      message: 'Version history could not open. Reload the page and try again.',
      id: 'history-rail-slot-missing'
    });
    return false;
  }

  const id = projectId || store.getState()?.projectId || '';
  if (!id) {
    // Not an error: a local draft genuinely has no cloud versions yet. Say the
    // thing that makes it have them, rather than "No active project" — which
    // was the modal's copy and told nobody what to do about it.
    showToast('Save this project to the cloud to start keeping version history.', 'info');
    return false;
  }
  slot.replaceChildren(buildFrame(store.getState()?.projectName || 'This project'));
  slot.hidden = false;

  // R3: tuck the left panel to a 48px gutter. This is also what returns
  // --pb-canvas-shift-left to 0 (history-rail.css), without which the
  // translated canvas would run under the rail — R1's actual failure mode.
  // `.pb-collapsed` is shell.js's; `.pb-rail-tucked` is ours, so the two states
  // compose instead of fighting and untucking restores what the user left.
  setLeftPanelTuckOwner('history', true);

  // `.pb-rail-open` drives the column layout below the dock width. A class
  // rather than `:has()` so the behaviour does not depend on selector support.
  const main = document.querySelector('.pb-main');
  main?.classList.add('pb-rail-open');
  const isDock = window.innerWidth <= MOBILE_DOCK_WIDTH;
  slot.dataset.detent = isDock ? 'half' : 'full';

  /**
   * Escape is LAYERED (contract §1): previewing → browsing → closed. It never
   * does both steps, and it never closes the rail while a version is on the
   * canvas — that would discard the user's position in the timeline silently.
   *
   * The shell's own capture-phase Escape handler stands down for this owner
   * (`_agentReviewSession.owner === 'history'`), so the two do not race.
   */
  const keydown = (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    event.preventDefault();
    if (preview.version !== null) {
      timeline.select(null);
      return;
    }
    closeHistoryRail('escape');
  };
  document.addEventListener('keydown', keydown);

  const restorer = createRestoreController({
    projectId: id,
    // The canvas is showing a DETACHED state; it has to come back to live
    // before the store changes underneath it.
    onBeforeApply: () => preview.release('restoring'),
    onApplied: async () => {
      if (session?.timeline !== timeline) return;
      timeline.select(null);
      await model.load();
      timeline.render();
    }
  });

  const comparer = createCompareController({
    projectId: id,
    // HR-7: the model already holds every row's contentHash — it is what
    // `foldInfo` folds runs on — so the comparer can answer an identical pair
    // from the hash instead of downloading two bundles to rediscover it.
    // Resolved through the model rather than captured, because `loadOlder`
    // grows the list under it.
    hashOf: (version) =>
      model.versions.find((v) => Number(v.version) === Number(version))?.contentHash || null
  });

  /**
   * Paint (or clear) the changed-section chrome for the current selection.
   *
   * The chrome is a FULL REPLACEMENT and the shell replays it on
   * `iframe:bridge-ready`, so clearing is `rows: []` rather than bookkeeping —
   * and clearing must happen on every path that leaves 'changes', including
   * selecting a different version and closing the rail. A stale outline on a
   * version that did not change those sections is a lie the user has no way to
   * detect.
   */
  async function paintChanges() {
    if (session?.reviewBar !== reviewBar) return;
    const version = preview.version;
    if (version === null || reviewBar.mode !== 'changes') {
      comparer.cancel();
      eventBus.emit('preview:changed-sections', { active: false, rows: [] });
      reviewBar.changes(null);
      return;
    }
    // The baseline is the version immediately before this one IN THE LOADED
    // WINDOW. If older versions have not been paged in there is no predecessor
    // to compare against, and saying so is better than comparing to the wrong one.
    const list = model.versions;
    const at = list.findIndex((v) => Number(v.version) === version);
    const previous = at >= 0 && list[at + 1] ? Number(list[at + 1].version) : null;

    reviewBar.changes('Comparing…');
    try {
      const result = await comparer.resolve(version, previous);
      if (result.stale || session?.reviewBar !== reviewBar) return;
      if (preview.version !== version || reviewBar.mode !== 'changes') return;
      eventBus.emit('preview:changed-sections', { active: true, rows: result.rows });
      reviewBar.changes(describeChanges(result));
    } catch (err) {
      if (session?.reviewBar !== reviewBar) return;
      reviewBar.changes('Could not work out what changed');
      reportError('History rail compare', err, { user: 'silent', id: 'history-rail-compare' });
    }
  }

  const reviewBar = mountReviewBar({
    onExit: () => timeline.select(null),
    onMode: () => void paintChanges(),
    onVisualDiff: () => {
      const version = preview.version;
      const latest = Number(model.versions[0]?.version);
      if (version === null || !Number.isInteger(latest) || latest === version) return;
      void import('../visual-diff-modal.js')
        .then((mod) => mod.showVisualDiffModal(id, version, latest))
        .catch((err) =>
          reportError('Visual diff open', err, {
            user: 'toast',
            message: 'Could not open the visual diff.'
          })
        );
    },
    onRestore: async () => {
      const version = preview.version;
      if (version === null) return;
      await restorer.restore(version);
    },
    // A read-only tab is told UP FRONT, with the reason on the control, rather
    // than after the click. The modal's guard only spoke once you had acted,
    // which is a trap with a receipt.
    /**
     * Can this project be written RIGHT NOW, ignoring the rail's own preview?
     *
     * `store.isWritable()` is false during a preview by design — the rail is
     * holding DETACHED_PREVIEW — so asking it directly disables the button in
     * exactly the state where restoring is the point. (Caught in HR-3's browser
     * pass: the control read "This tab is read-only" the moment a version was
     * selected.) Restore is the action that ENDS the preview, so its own
     * blocker is not an obstacle to it; anything else is, and says why.
     */
    canRestore: () => {
      const others = store
        .getBlockers()
        .filter((blocker) => blocker !== WRITE_BLOCKER.DETACHED_PREVIEW);
      return others.length ? readOnlyReasonFor(others[0]) : null;
    }
  });

  const preview = createPreviewController({
    projectId: id,
    onChange: (state) => {
      if (session?.preview !== preview) return;
      const row = model.versions.find((v) => Number(v.version) === state.version);
      reviewBar?.set({
        ...state,
        label: row?.label || null,
        when: row ? `${dayLabelOf(row.createdAt)} ${clockTime(row.createdAt)}` : null
      });
      if (state.status === 'previewing' || state.status === 'live') void paintChanges();
      // Touch has no hover, so warm the NEIGHBOURS of whatever is selected —
      // the versions ↑/↓ and the next tap are most likely to reach. This is the
      // honest touch analogue of hover prefetch, and it helps desktop too.
      if (state.status === 'previewing') warmNeighbours(state.version);
    }
  });

  const model = createVersionModel({ projectId: id, listVersions: listProjectVersions });
  /**
   * Fill a row's summary with what that version actually changed.
   *
   * The modal put this behind a 24px chevron with a native tooltip. It is the
   * row's real content — "Hero, Services · +1 section" is what turns a list of
   * numbers into a list of events — so it is always visible, and lazily filled
   * because the diff needs two bundles.
   */
  async function fillSummaries() {
    if (session?.timeline !== timeline) return;
    const list = model.versions;
    for (let i = 0; i < list.length; i += 1) {
      const version = Number(list[i].version);
      const previous = list[i + 1] ? Number(list[i + 1].version) : null;
      try {
        const result = await comparer.summaryFor(version, previous);
        if (session?.timeline !== timeline) return;
        timeline.setSummary(version, describeChanges(result, { terse: true }));
      } catch {
        // Expected-flow fallback: a summary that cannot be computed leaves the
        // row's existing caption alone rather than showing an error per row.
        return;
      }
    }
  }

  /** Warm the versions adjacent to `version` so the next step is already there. */
  function warmNeighbours(version) {
    const list = model.versions;
    const at = list.findIndex((v) => Number(v.version) === Number(version));
    if (at < 0) return;
    for (const neighbour of [list[at - 1], list[at + 1]]) {
      if (!neighbour) continue;
      void getVersionState(id, Number(neighbour.version)).catch((err) => {
        reportError('Version prefetch', err, {
          user: 'silent',
          id: 'history-rail-prefetch',
          detail: `warming v${neighbour.version} failed`
        });
      });
    }
  }

  const timeline = mountTimeline({
    host: slot.querySelector('[data-role="rail-body"]'),
    model,
    liveState: () => ({ dirty: store.isDirty() }),
    onSave: () => eventBus.emit('toolbar:cloud-save'),
    // Warm the LRU so selecting costs nothing. Fire-and-forget by design: a
    // failed prefetch must be invisible — the click path reports for itself.
    onHover: (version) => {
      // A prefetch is speculative, so its failure must be invisible to the USER
      // — but not invisible full stop. An empty rejection handler is prohibited
      // in this repo precisely because it makes a failing seam undiagnosable;
      // `user: 'silent'` shows nothing while still reporting, and the dedupe id
      // keeps a broken transport from filling the log one hover at a time.
      // (Worded without quoting the forbidden syntax: the consistency sweep
      // reads raw text, so an example of what NOT to do trips it.)
      void getVersionState(id, version).catch((err) => {
        reportError('Version prefetch', err, {
          user: 'silent',
          id: 'history-rail-prefetch',
          detail: `warming v${version} failed`
        });
      });
    },
    onRename: (version) => void renameVersion(version),
    // Restore from the mobile footer. Same controller, same single-flight, same
    // transaction as the review bar's button — there is one restore, reachable
    // from wherever the thumb is.
    onCommit: async (version) => {
      if (version === null) return;
      await restorer.restore(version);
    },
    canCommit: () => {
      const others = store
        .getBlockers()
        .filter((blocker) => blocker !== WRITE_BLOCKER.DETACHED_PREVIEW);
      return others.length ? readOnlyReasonFor(others[0]) : null;
    },
    // Contract §3: selection PREVIEWS. It never mutates the project — the only
    // commit control on screen is the review bar's button.
    onSelect: (version) => {
      void preview.select(version);
      eventBus.emit('history-rail:selected', { projectId: id, version });
    }
  });

  /**
   * The page-fallback notice can only come from the shell — it is the side that
   * knows which pages the target version actually has. Without this the fallback
   * works SILENTLY, which is the bug the notice exists to prevent: the user asked
   * for About, got Home, and nothing said why. (Caught in HR-2's browser pass,
   * where v4 predated the About page and the bar stayed quiet.)
   */
  const onShellPreviewState = (payload = {}) => {
    if (session?.reviewBar !== reviewBar) return;
    if (payload.owner !== 'history' || !payload.active) return;
    const fallback = payload.pageFallback || null;
    if (fallback?.requested) {
      // The shell reports page IDs — it is the side that knows the version's
      // pages. The NAME of the page the user asked for is only knowable here,
      // from the live state they came from.
      const live = store.getState()?.pages?.find((page) => page.id === fallback.requested);
      fallback.requestedName = live?.name || null;
    }
    reviewBar?.note(fallback, preview.version);
  };
  eventBus.on('agent:review-state-changed', onShellPreviewState);

  session = {
    restorer,
    slot,
    keydown,
    projectId: id,
    model,
    timeline,
    preview,
    reviewBar,
    onShellPreviewState
  };
  eventBus.emit('history-rail:opened', { projectId: id });

  /**
   * Name a version, INLINE — the `revision-panel.js` idiom, not a dialog.
   *
   * `labelProjectVersion()` has shipped since UX Pack F1b and no surface ever
   * offered it, so named checkpoints were half-built and invisible. A labelled
   * version is a Milestone, which is what makes the Milestones filter worth
   * having.
   *
   * Inline rather than a modal because the plan is explicit about it: naming a
   * version is not a separate flow, and a dialog for one short string is the
   * kind of ceremony that stops people bothering.
   */
  async function renameVersion(version) {
    const row = model.versions.find((v) => Number(v.version) === Number(version));
    if (!row) return;
    const host = timeline.labelHostFor(version);
    if (!host || host.dataset.editing === '1') return;

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'pb-input pb-rail-rename';
    input.maxLength = 80;
    input.value = row.label || '';
    input.setAttribute('aria-label', `Name version ${version}`);
    host.dataset.editing = '1';
    host.replaceChildren(input);
    input.focus();
    input.select();

    let settled = false;
    const finish = async (commit) => {
      if (settled) return;
      settled = true;
      const next = String(input.value || '').trim();
      delete host.dataset.editing;
      if (!commit || !next || next === (row.label || '')) {
        timeline.render();
        return;
      }
      try {
        await labelProjectVersion(id, version, next);
      } catch (err) {
        reportError('Version rename', err, {
          user: 'toast',
          message: err instanceof TemplateApiError ? err.message : 'Could not name that version'
        });
        timeline.render();
        return;
      }
      if (session?.timeline !== timeline) return;
      // The server accepted it, so reflect it without refetching the window.
      row.label = next;
      timeline.render();
      showToast(`Named v${version}`, 'success');
    };

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        void finish(true);
      } else if (event.key === 'Escape') {
        // Stop here: the rail's own Escape layering would otherwise close the
        // preview or the rail while the user only meant to abandon a rename.
        event.preventDefault();
        event.stopPropagation();
        void finish(false);
      }
    });
    input.addEventListener('blur', () => void finish(true));
  }

  timeline.renderSkeleton();
  model
    .load()
    .then(() => {
      // The rail may have closed, or reopened on another project, while the
      // list was in flight. Either way this result is not ours to paint.
      if (session?.timeline !== timeline) return;
      timeline.render();
      timeline.focusNewest();
      void fillSummaries();
    })
    .catch((err) => {
      if (session?.timeline !== timeline) return;
      timeline.renderError(err instanceof TemplateApiError ? err.message : null);
      reportError('History rail versions', err, { user: 'silent', id: 'history-rail-load' });
    });

  return true;
}

/**
 * Every exit path lands here, and it restores everything `open` changed. HR-2
 * adds the detached-preview release; keeping one exit now is what makes that a
 * one-line addition rather than a second teardown.
 *
 * @param {string} reason
 */
export function closeHistoryRail(reason = 'close') {
  if (!session) return false;
  const { slot, keydown, projectId, timeline, preview, reviewBar, onShellPreviewState } = session;
  session = null;

  eventBus.off?.('agent:review-state-changed', onShellPreviewState);

  // Order matters: release the canvas BEFORE tearing down the chrome that
  // explains why it was detached. The reverse leaves a read-only canvas with
  // nothing on screen saying so.
  // Clear the canvas chrome BEFORE the bar that explains it goes away, and
  // before the preview releases — a full-replacement empty set is the teardown.
  eventBus.emit('preview:changed-sections', { active: false, rows: [] });
  preview?.destroy();
  reviewBar?.destroy();
  timeline?.destroy();
  document.removeEventListener('keydown', keydown);
  setLeftPanelTuckOwner('history', false);
  document.querySelector('.pb-main')?.classList.remove('pb-rail-open');
  slot.hidden = true;
  delete slot.dataset.detent;
  slot.replaceChildren();

  eventBus.emit('history-rail:closed', { reason, projectId });
  return true;
}

function buildFrame(projectName) {
  const frag = document.createDocumentFragment();

  const head = document.createElement('div');
  head.className = 'pb-rail-head';

  const titles = document.createElement('div');
  const title = document.createElement('div');
  title.className = 'pb-rail-title';
  title.textContent = 'History';
  const context = document.createElement('div');
  context.className = 'pb-rail-context';
  // PP-2's rule reaches this surface too: a dialog that acts somewhere states
  // its target. The rail's target is the project it is showing the history of.
  context.textContent = projectName;
  titles.append(title, context);

  const actions = document.createElement('div');
  actions.className = 'pb-rail-head-actions';
  actions.appendChild(
    createButton({
      variant: 'icon',
      icon: icon('close', { size: 'sm' }),
      ariaLabel: 'Close history',
      tooltip: 'Close history',
      onClick: () => closeHistoryRail('close-button')
    })
  );

  head.append(titles, actions);

  const body = document.createElement('div');
  body.className = 'pb-rail-body';
  body.setAttribute('data-role', 'rail-body');

  frag.append(head, body);
  return frag;
}

/** Test seam: drop all rail state without touching the DOM contract. */
export function __resetHistoryRailForTests() {
  if (session) closeHistoryRail('test-reset');
  session = null;
}
